import {
  useFieldArray,
  UseFieldArrayRemove,
  useFormContext,
} from 'react-hook-form';

import { EventRequest } from '../interfaces/events';
import { Wine } from '../interfaces/strapiComponents/wine';
import InputPresentational from './Form/InputPresentational';

import { MinusCircleIcon } from '@heroicons/react/outline';
import StaticInputPresentational from './Form/StaticInputPresentational';
import { formatCurrency } from '../libs/utils/helpers';
import H4SpacingLetter from './Typography/H4SpacingLetter';
import UnderlineButton from './Buttons/UnderlineButton';
import {
  normalizeGlassesPerBottle,
  MAX_GLASSES_PER_BOTTLE,
} from '../pages/Eventi/utils/financialValidation';
import { toast } from 'react-toastify';

const emptyWine: Wine = {
  wine: '',
  estimatedPricePerBottle: 0,
  estimatedBottles: 0,
  estimatedGlassesPerBottle: 20,
  finalPricePerBottle: 0,
  finalBottles: 0,
  finalGlassesPerBottle: 20,
};

const ListaVini = ({ disabled = false }) => {
  const { watch, control } = useFormContext<EventRequest>();
  const wines = watch('wines') || [];

  const wineTypes = wines?.length;

  const estimatedBottles = wines.reduce((estimatedBottles, wine) => {
    return estimatedBottles + wine.estimatedBottles;
  }, 0);

  const estimatedTotal = wines.reduce((estimatedTotal, wine) => {
    const wineTotal = wine.estimatedBottles * wine.estimatedPricePerBottle;
    return estimatedTotal + wineTotal;
  }, 0);

  const finalBottles = wines.reduce((finalBottles, wine) => {
    return finalBottles + wine.finalBottles;
  }, 0);

  const finalTotal = wines.reduce((finalTotal, wine) => {
    const wineTotal = wine.finalBottles * wine.finalPricePerBottle;
    return finalTotal + wineTotal;
  }, 0);

  const useWineArray = useFieldArray({
    control,
    name: 'wines',
  });
  const addWine = () => useWineArray.append(emptyWine);

  return (
    <div>
      <div className='grid grid-cols-[80px,repeat(12,minmax(0,1fr))] gap-6 items-end'>
        {useWineArray.fields.length > 0 && <WineHeader />}
        {useWineArray.fields.map((wine, wineIdx) => (
          <WineRow
            key={wine.id}
            wineIdx={wineIdx}
            removeWine={useWineArray.remove}
            disabled={disabled}
          />
        ))}
      </div>
      <div className='my-6 w-full flex justify-center'>
        <UnderlineButton onClick={addWine} disabled={disabled}>
          Aggiungi vino
        </UnderlineButton>
      </div>
      <div className='flex w-full justify-between gap-6'>
        <div className='space-y-4 bg-neutral-100 px-3 py-4 w-full'>
          <H4SpacingLetter>Totale vini preventivo</H4SpacingLetter>
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
            <StaticInputPresentational
              big
              label='Tipologie di vino'
              text={wineTypes}
            />
            <StaticInputPresentational
              big
              label='Totale bottiglie'
              text={estimatedBottles}
            />
            <StaticInputPresentational
              big
              label='Totale spesa'
              text={formatCurrency(estimatedTotal)}
            />
          </div>
        </div>

        <div className='space-y-4 bg-neutral-100 px-3 py-4 w-full'>
          <H4SpacingLetter>Totale vini consuntivo</H4SpacingLetter>
          <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
            <StaticInputPresentational
              big
              label='Tipologie di vino'
              text={wineTypes}
            />
            <StaticInputPresentational
              big
              label='Totale bottiglie'
              text={finalBottles}
            />
            <StaticInputPresentational
              big
              label='Totale spesa'
              text={formatCurrency(finalTotal)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const WineHeader = () => (
  <>
    <div className='col-span-4'>
      <H4SpacingLetter>Lista vini</H4SpacingLetter>
    </div>
    <div className='col-span-4'>
      <H4SpacingLetter>Bilancio preventivo</H4SpacingLetter>
    </div>
    <div className='col-span-4'>
      <H4SpacingLetter>Bilancio consuntivo</H4SpacingLetter>
    </div>
  </>
);

interface WineRowProps {
  wineIdx: number;
  removeWine: UseFieldArrayRemove;
  disabled?: boolean;
}

const WineRow: React.FC<WineRowProps> = ({
  wineIdx,
  removeWine,
  disabled = false,
}) => {
  const { watch, register, setValue } = useFormContext<EventRequest>();
  const wine = watch('wines')[wineIdx];

  const normalizeGlassesInput =
    (
      fieldName:
        | `wines.${number}.estimatedGlassesPerBottle`
        | `wines.${number}.finalGlassesPerBottle`,
    ) =>
    (event: React.FocusEvent<HTMLInputElement>) => {
      const rawValue = Number(event.target.value);
      const normalizedValue = normalizeGlassesPerBottle(event.target.value);

      if (Number.isFinite(rawValue) && rawValue > MAX_GLASSES_PER_BOTTLE) {
        toast.error(
          `Il numero di calici per bottiglia non può superare ${MAX_GLASSES_PER_BOTTLE}`,
        );
      }

      setValue(fieldName, normalizedValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
    };

  return (
    <>
      <div className='col-span-4 items-end flex gap-3'>
        <div className='rounded-full border-2 border-primary w-14 h-12 flex items-center justify-center'>
          <p className='text-2xl font-bold text-primary'>{wineIdx + 1}</p>
        </div>
        <InputPresentational
          label='Azienda - Denominazione - Annata'
          type='text'
          id={`wines.${wineIdx}.wine`}
          name={`wines.${wineIdx}.wine`}
          register={register}
          disabled={disabled}
        />
      </div>

      <InputPresentational
        label='Costo (iva incl.)'
        type='number'
        id={`wines.${wineIdx}.estimatedPricePerBottle`}
        name={`wines.${wineIdx}.estimatedPricePerBottle`}
        register={register}
        disabled={disabled}
      />

      <InputPresentational
        label='N. Bottiglie'
        type='number'
        disabled
        value={wine.estimatedBottles}
      />

      <InputPresentational
        label='N. calici per bottiglia'
        type='number'
        id={`wines.${wineIdx}.estimatedGlassesPerBottle`}
        name={`wines.${wineIdx}.estimatedGlassesPerBottle`}
        register={register}
        disabled={disabled}
        min={1}
        max={20}
        step={1}
        onBlur={normalizeGlassesInput(
          `wines.${wineIdx}.estimatedGlassesPerBottle`,
        )}
      />

      <InputPresentational
        disabled
        label='Totale'
        type='number'
        value={wine.estimatedPricePerBottle * wine.estimatedBottles}
      />

      {/* Bilancio consuntivo */}

      <InputPresentational
        label='Costo (iva incl.)'
        type='number'
        id={`wines.${wineIdx}.finalPricePerBottle`}
        name={`wines.${wineIdx}.finalPricePerBottle`}
        register={register}
        disabled={disabled}
      />

      <InputPresentational
        label='N. Bottiglie'
        type='number'
        disabled
        value={wine.finalBottles}
      />

      <InputPresentational
        label='Numero calici per bottiglia'
        type='number'
        id={`wines.${wineIdx}.finalGlassesPerBottle`}
        name={`wines.${wineIdx}.finalGlassesPerBottle`}
        register={register}
        disabled={disabled}
        min={1}
        max={20}
        step={1}
        onBlur={normalizeGlassesInput(`wines.${wineIdx}.finalGlassesPerBottle`)}
      />

      <InputPresentational
        disabled
        label='Totale'
        type='number'
        value={wine.finalPricePerBottle * wine.finalBottles}
      />

      <div className='flex items-end h-full pb-2'>
        <MinusCircleIcon
          className={`text-primary ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          width={24}
          onClick={() => {
            if (disabled) return;
            removeWine(wineIdx);
          }}
        />
      </div>
    </>
  );
};

export default ListaVini;
